import { NAME as ACCOUNT_NAME } from '@app/account/constants';
import { NAME as MARKETING_NAME } from '@app/marketing/constants';
import { SHARED_FUNNEL_PLAN_ID } from '@app/pricing/constants';

// Hooks
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import { usePartnership } from '@app/account/hooks/usePartnership';
import { usePlanIdWithCurrency } from '@app/pricing/hooks/usePlanIdWithCurrency';

// Utils
import { getAssetsUrls } from '@core/contentful/helper';
import { fetchMarketingData } from '@core/contentful/fetchMarketingData';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';

// UI
import MetaTags from '@ui/components/MetaTags';
import SharedLayout from '@ui/layouts/Shared.layout';
import SignupForm from '@app/account/components/SignupForm';
import SharedIntro from '@app/marketing/components/SharedIntro';

// Types
import type { NextPage } from 'next';
import type { ContentfulPageProps } from '@core/types';

export const SharedFunnelSignupPage: NextPage<ContentfulPageProps> = ({
    testimonials,
    logoUrls,
}) => {
    const { t } = useTranslation(MARKETING_NAME);
    const router = useRouter();
    const { locale, pathname, query, isReady } = router;
    const { campaignId } = query;

    const fullPlanId = usePlanIdWithCurrency(SHARED_FUNNEL_PLAN_ID);
    const { trialDuration } = usePartnership();

    useEffect(() => {
        if (isReady && !campaignId) {
            router.push('/404');
        }
    }, [router, campaignId, isReady]);

    return (
        <>
            <MetaTags
                title={t(`${ACCOUNT_NAME}:page-title-signup`)}
                description={t(`${ACCOUNT_NAME}:page-description-signup`)}
                locale={locale}
                pathname={pathname}
            />

            <SharedLayout testimonials={testimonials} logoUrls={logoUrls}>
                <SharedIntro
                    headline={t('shared-funnel-signup-title', { trialDuration })}
                    subtitle={t('shared-funnel-signup-description')}
                />

                <SignupForm
                    loginUrl="/shared/login"
                    successRedirectUrl="/shared/checkout"
                    fixedPlanId={fullPlanId}
                    buttonText={t(`${ACCOUNT_NAME}:shared-signup-button-text`)}
                    consentTransKey={`${ACCOUNT_NAME}:consent-shared-funnel`}
                />
            </SharedLayout>
        </>
    );
};

export default SharedFunnelSignupPage;

export const getStaticProps = async ({ locale }) => {
    const response = await fetchMarketingData(locale);

    return {
        props: {
            ...(await serverSideTranslations(locale, [MARKETING_NAME, ACCOUNT_NAME, 'common'])),
            // Will be passed to the page component as props
            logoUrls: getAssetsUrls(response.fields.logos),
            testimonials: response.fields.testimonialSlider,
        },
    };
};
